/** @jsx jsx */
import { jsx } from '@theme-ui/core';
import { Props as SelectProps, components, IndicatorProps, OptionTypeBase } from 'react-select';

import { useTheme } from 'src/theme';
import { BASE_STYLES } from 'src/shared/base-styles';
import { Translation } from 'src/shared/translation';
import { DropdownIndicatorIcon } from 'src/shared/components/icons';

export function useSharedSelectProps<Options extends OptionTypeBase>({
    placeholderCode,
    noOptionsCode,
    noDropdownIndicator,
    hasError,
    overrideStyles,
}: {
    placeholderCode: string;
    noOptionsCode: string;
    noDropdownIndicator?: boolean;
    hasError: boolean;
    overrideStyles?: {
        [k in 'control' | 'singleValue']?: {};
    };
}): Pick<
    SelectProps<Options>,
    | 'placeholder'
    | 'components'
    | 'noOptionsMessage'
    | 'isClearable'
    | 'blurInputOnSelect'
    | 'styles'
    | 'theme'
    | 'menuPortalTarget'
> {
    const { theme } = useTheme();

    return {
        placeholder: <Translation code={placeholderCode} />,
        components: {
            DropdownIndicator: noDropdownIndicator ? () => null : DropdownIndicator,
        },
        noOptionsMessage: () =>
            // TypeScript type is incorrect, function can return JSX
            (<Translation code={noOptionsCode} />) as any as string,
        isClearable: false,
        blurInputOnSelect: true,
        menuPortalTarget: document.body,
        styles: {
            // @ts-ignore
            menuPortal: (provided) => ({
                ...provided,
                zIndex: 100,

                // the below line is needed, since we added menuPortalTarget: document.body, and so all the previously inherited CSS properties of menuPortal are lost and need to be explicitly defined
                ...BASE_STYLES,

                // the below lines are needed, since BASE_STYLES is an theme-ui SX object and not all its values can be used here
                fontSize: theme.fontSizes[2],
                color: `${theme.colors.nearMeCheck.grey.rich} !important`,
                lineHeight: 1.5,
                fontFamily: theme.fonts.normal,
            }),
            valueContainer: (provided) => ({
                ...provided,
                padding: '5px 0',
            }),
            control: (provided, { isFocused }) => {
                const normalBorderColor = hasError
                    ? theme.colors.whitelabel.failure
                    : theme.colors.nearMeCheck.grey[7];

                const borderColor = isFocused
                    ? theme.colors.nearMeCheck.blue.light
                    : normalBorderColor;

                return {
                    ...provided,
                    border: 'none',
                    borderBottom: theme.borders[1],
                    borderBottomColor: borderColor,
                    outline: 'none',
                    backgroundColor: 'transparent',
                    minHeight: '35px',
                    boxShadow: 'none',
                    ...overrideStyles?.control,

                    '&:hover': {
                        borderBottomColor: borderColor,
                    },
                };
            },
            input: (provided) => ({
                ...provided,
                margin: 0,
                padding: 0,
                fontSize: theme.fontSizes[2],
                color: theme.colors.nearMeCheck.grey[10],
            }),
            dropdownIndicator: (provided) => ({
                ...provided,
                paddingTop: '10px',
            }),
            indicatorSeparator: () => ({
                display: 'none',
            }),
            placeholder: (provided) => ({
                ...provided,
                margin: 0,
                fontSize: theme.fontSizes[2],
                fontStyle: 'italic',
                color: theme.colors.nearMeCheck.grey[7],
                userSelect: 'none',
            }),
            singleValue: (provided) => ({
                ...provided,
                top: 'auto',
                transform: 'none',
                margin: 0,
                fontSize: theme.fontSizes[2],
                color: hasError
                    ? theme.colors.whitelabel.failure
                    : theme.colors.nearMeCheck.grey[10],
                ...overrideStyles?.singleValue,
            }),
            option: (provided) => ({
                ...provided,
                fontSize: theme.fontSizes[2],
            }),
        },
        theme: (themeSelect) => ({
            ...themeSelect,
            colors: {
                ...themeSelect.colors,
                primary: theme.colors.nearMeCheck.blue.light,
                primary25: theme.colors.nearMeCheck.grey[1], // option hover
                primary50: theme.colors.nearMeCheck.grey[2], // option active
                neutral20: theme.colors.nearMeCheck.grey.rich, // dropdown indicator default
                neutral40: theme.colors.nearMeCheck.grey[3], // dropdown indicator hover
                neutral60: theme.colors.nearMeCheck.grey[3], // dropdown indicator open
                neutral80: theme.colors.nearMeCheck.grey[3], // dropdown indicator open + hover
                danger: theme.colors.whitelabel.failure,
            },
            spacing: {
                ...themeSelect.spacing,
                menuGutter: 1,
            },
            borderRadius: 0,
        }),
    };
}

function DropdownIndicator(props: IndicatorProps<any, boolean>) {
    return (
        <components.DropdownIndicator {...props}>
            <DropdownIndicatorIcon />
        </components.DropdownIndicator>
    );
}
